"use client";

import React, { useState, useCallback } from 'react';
import styles from './_gc-conversion-calculator.module.scss';
import Button from '@/components/button';
import CustomImage from '@/components/image';
import PageAnchor from '@/components/page-anchor';
import Badge from '@/components/badge';
import GcConversionStep from './steps/gc-conversion-step';

/**
 * @description HCP Dosing — GC Conversion Calculator
 * Intro screen: left — title, description, CTA; right — pill bottles graphic.
 * Figma node: 40000014:30054
 */
const INITIAL_FORM = {};

function HcpGcConversionCalculator({ ref, className }) {
  const [hasStarted, setHasStarted] = useState(false);
  const [formValues, setFormValues] = useState(INITIAL_FORM);

  const sectionClass = `${styles.gc_conversion_calculator}${hasStarted ? ` ${styles.gc_conversion_calculator__active}` : ''}${className ? ` ${className}` : ''}`;

  const handleStart = useCallback(() => {
    setHasStarted(true);
  }, []);

  const handleNext = useCallback((values) => {
    setFormValues(values);
  }, []);

  const handleReset = useCallback(() => {
    setHasStarted(false);
    setFormValues(INITIAL_FORM);
  }, []);

  return (
    <>
      <PageAnchor anchorId="gc-conversion-calculator" offsetY={-32} />
      <section
        ref={ref}
        className={sectionClass}
        data-gtm-event-category="gc-conversion-calculator"
      >
        <div className="container container--px-16">
          <div className={styles.calculator_layout}>

            {/* ── Content column ── */}
            <div className={styles.content_col}>
              <Badge ariaLabel="New">NEW</Badge>

              {!hasStarted ? (
                // ── Intro / default state ──────────────────────────────
                <div className={styles.intro_content}>
                  <h2 className={styles.intro_heading}>
                    GC Conversion Calculator
                  </h2>

                  <p className={styles.intro_description}>
                    When considering switching your patient from one type of GC to
                    another, the GC Conversion Calculator can help you determine the
                    equivalent dose.
                  </p>

                  <Button
                    type="button"
                    variant={['solid', 'solid--eggplant', 'hcp-big']}
                    onClick={handleStart}
                    data-gtm-event-category="gc-conversion-calculator"
                    data-gtm-event-action="click"
                    data-gtm-event-label="Calculate GC Conversion"
                  >
                    Calculate now
                  </Button>
                </div>
              ) : (
                // ── Calculator steps ───────────────────────────────────
                <GcConversionStep
                  onNext={handleNext}
                  onReset={handleReset}
                />
              )}
            </div>

            {/* ── Image column — always visible ── */}
            <div className={styles.image_col}>
              <CustomImage
                src="/images/components/hcp/conversion-calc/Dosing_GC-Conversion-Calculator.png"
                alt=""
                width={382}
                height={366}
                className={styles.graphic}
              />
            </div>

          </div>
        </div>
      </section>
    </>
  );
}

export default HcpGcConversionCalculator;